import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Rocket } from 'lucide-react';
import GradientButton from '../ui/GradientButton';

export default function CTABanner() {
  const scrollTo = (href) => { const el = document.querySelector(href); if (el) el.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <section className="relative py-32 md:py-40 overflow-hidden" style={{ background: 'transparent' }}>
      {/* Animated background glow */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear',
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-r from-[#00D4FF]/5 to-[#7B61FF]/5 blur-3xl"
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          {/* Animated badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[rgba(123,97,255,0.08)] border border-[rgba(123,97,255,0.2)] rounded-full mb-6"
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            >
              <Rocket className="w-4 h-4 text-[#7B61FF]" />
            </motion.div>
            <span className="text-[#7B61FF] text-sm font-medium">Let's Build Together</span>
          </motion.div>

          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-[clamp(2rem,5vw,4rem)] font-bold font-space text-[#F0F0F5] tracking-[-0.02em] leading-[1.1] mb-6"
          >
            Ready to Build Something{' '}<br className="hidden sm:block" />
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="gradient-text inline-block"
            >
              Extraordinary
            </motion.span>?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-[#8A8AA3] text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10"
          >
            Let's turn your vision into reality. We partner with ambitious teams to build products that scale and endure.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.7 }}
            viewport={{ once: true }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <GradientButton onClick={() => scrollTo('#contact')} className="!px-10 !py-4 !text-base !rounded-full group" data-testid="cta-start-project">
                <span className="flex items-center gap-2">
                  Start Your Project
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.div>
                </span>
              </GradientButton>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
